import React, { useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  LabelList,
  ResponsiveContainer,
} from "recharts";
import { useDashboard } from "../Hooks/useDashboard";

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white shadow-lg border rounded-md p-2 w-40">
        <p className="text-gray-500 text-sm">{label}</p>
        <p className="text-blue-600 font-semibold">
          Completion: {payload[0].value}%
        </p>
      </div>
    );
  }
  return null;
};

const CompletionBarChart = ({ filter }) => {
  const [projects, setProjects] = useState([]);
  const { fetchProjectCompletion, loading } = useDashboard();

  const fetchData = async () => {
    const res = await fetchProjectCompletion({ project: filter });
    setProjects(res);
  };

  useEffect(() => {
    fetchData();
  }, [filter]);

  // Skeleton cards while loading
  const skeletonCards = new Array(3).fill(0);

  if (loading) {
    return (
      <div className="flex justify-between gap-4 min-h-[360px]">
        {skeletonCards.map((_, idx) => (
          <div
            key={idx}
            className="w-[33%] bg-gray-200 rounded-xl shadow animate-pulse"
          />
        ))}
      </div>
    );
  }

  if (!projects || projects.length === 0) {
    return (
      <div className="text-gray-400 text-center py-20">
        No project completion data available
      </div>
    );
  }

  return (
    <div className="w-full h-max">
      <h2 className="text-xl font-semibold mb-4">Project Completion (%)</h2>
      <div className="w-full overflow-x-auto custom-scroll">
        <div
          style={{
            width: `max(${projects.length * 170}px, 750px, 47vw)`,
            height: 360,
          }}
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={projects}
              margin={{ top: 20, right: 0, left: 0, bottom: 0 }}
            >
              <XAxis
                dataKey="name"
                angle={0} // no angle needed for wrapping
                textAnchor="middle"
                interval={0}
                height={80}
                tick={({ x, y, payload }) => {
                  const maxWidth = 80; // maximum width for each line
                  const words = payload.value.split(" ");
                  const lines = [];
                  let currentLine = "";

                  words.forEach((word) => {
                    const testLine = currentLine
                      ? currentLine + " " + word
                      : word;
                    // Rough estimate of text width (12px font size)
                    if (testLine.length * 6 < maxWidth) {
                      currentLine = testLine;
                    } else {
                      lines.push(currentLine);
                      currentLine = word;
                    }
                  });
                  if (currentLine) lines.push(currentLine);

                  return (
                    <g transform={`translate(${x},${y + 10})`}>
                      {lines.map((line, index) => (
                        <text
                          key={index}
                          x={0}
                          y={index * 14} // line height
                          textAnchor="middle"
                          fill="#374151"
                          fontSize={12}
                        >
                          {line}
                        </text>
                      ))}
                    </g>
                  );
                }}
              />
              <YAxis domain={[0, 100]} tickFormatter={(tick) => `${tick}%`} />
              <Tooltip
                content={<CustomTooltip />}
                cursor={{ fill: "rgba(0,0,0,0.01)" }}
              />
              <Bar
                dataKey="completion_percentage"
                fill="#b8c0ff"
                radius={[8, 8, 0, 0]}
              >
                <LabelList
                  dataKey="completion_percentage"
                  position="top"
                  fontWeight={"bold"}
                  formatter={(v) => `${v}%`}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default CompletionBarChart;
